import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Button, Dialog, DialogActions,
  DialogContent, DialogTitle, TextField, IconButton,
  LinearProgress,
  MenuItem,
} from '@mui/material';
import { Delete, Edit } from '@mui/icons-material';
import axios from 'axios';
import api from '../../../api/axiosInterceptor';
import { API_BASE_URL } from '../../../config';
import TableComponent from '../../../Components/TableComponent/TableComponent';
import { LoadingSpinner } from '../../../Components/spinner/spinner';
import { SnackBarComponent } from '../../../Components/snackBar/snackBar';

// Fetch financial establishments for foreign key dropdown
const useFetchFeBaseList = () => {
  const [list, setList] = useState([]);
  useEffect(() => {
    axios
      .get(`${API_BASE_URL}/fe-mgmt/fe-base/`)
      .then(res => setList(res.data))
      .catch(() => setList([]));
  }, []);
  return list;
};

export const AdminFeAuditReportMasterComponent = () => {
  const feBaseList = useFetchFeBaseList();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('info');

  const [openFormDialog, setOpenFormDialog] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);

  const [formData, setFormData] = useState({
    financial_establishment: '',
    fy_year: '',
    file_type: '',
    doc: null,
  });

  const [formErrors, setFormErrors] = useState({});
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteItem, setDeleteItem] = useState(null);

  const columns = [
    { id: 'slNo', field: 'slNo', headerName: 'Sl No.', width: 70 },
    { id: 'financial_establishment', field: 'financial_establishment', headerName: 'FE Base', width: 200 },
    { id: 'fy_year', field: 'fy_year', headerName: 'FY Year', width: 150 },
    { id: 'file_type', field: 'file_type', headerName: 'File Type', width: 150 },
    {
      id: 'doc',
      field: 'doc',
      headerName: 'Document',
      width: 120,
      renderCell: (params) =>
        params.value ? (
          <a href={params.value} target="_blank" rel="noopener noreferrer">
            View
          </a>
        ) : (
          'No Doc'
        ),
    },
    {
      id: 'actions',
      field: 'actions',
      headerName: 'Actions',
      width: 120,
      renderCell: (params) => (
        <>
          <IconButton color="primary" onClick={() => handleEdit(params.row)} disabled={uploading}>
            <Edit />
          </IconButton>
          <IconButton color="error" onClick={() => handleDeleteConfirmation(params.row)} disabled={uploading}>
            <Delete />
          </IconButton>
        </>
      ),
    },
  ];

  const fetchAuditReports = () => {
    setLoading(true);
    axios
      .get(`${API_BASE_URL}/fe-mgmt/fe-audit-report/`)
      .then((response) => {
        const apiData = response.data.map((item, index) => {
          const feBaseObj = feBaseList.find(fe => fe.id === (item.financial_establishment?.id || item.financial_establishment));
          return {
            id: item.id,
            slNo: index + 1,
            financial_establishment: feBaseObj ? feBaseObj.name_of_fe : (item.financial_establishment?.name_of_fe || item.financial_establishment),
            fy_year: item.fy_year,
            file_type: item.file_type,
            doc: item.doc,
          };
        });
        setRows(apiData);
        setLoading(false);
      })
      .catch((error) => {
        console.error('Failed to fetch Audit Reports:', error);
        setSnackbarMessage('Failed to load Audit Reports.');
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
        setLoading(false);
      });
  };

  useEffect(() => {
    if (feBaseList.length > 0) {
      fetchAuditReports();
    }
  }, [feBaseList]);

  const handleSnackbarClose = () => setSnackbarOpen(false);

  const handleAddItem = () => {
    setIsEditMode(false);
    setFormData({
      financial_establishment: '',
      fy_year: '',
      file_type: '',
      doc: null,
    });
    setFormErrors({});
    setUploadProgress(0);
    setOpenFormDialog(true);
  };

  const handleEdit = (item) => {
    setIsEditMode(true);
    setSelectedItem(item);
    // Find FE Base record to set id for dropdown selection
    const feBaseObj = feBaseList.find(fe => fe.name_of_fe === item.financial_establishment || fe.id === item.financial_establishment);

    setFormData({
      financial_establishment: feBaseObj ? feBaseObj.id : '',
      fy_year: item.fy_year,
      file_type: item.file_type,
      doc: null, // File upload left empty for edit
    });
    setFormErrors({});
    setUploadProgress(0);
    setOpenFormDialog(true);
  };

  const handleDeleteConfirmation = (item) => {
    setDeleteItem(item);
    setDeleteDialogOpen(true);
  };

  const handleDelete = () => {
    api
      .delete(`/fe-mgmt/fe-audit-report/${deleteItem.id}/`)
      .then(() => {
        setSnackbarMessage('Audit Report deleted successfully.');
        setSnackbarSeverity('success');
        setSnackbarOpen(true);
        fetchAuditReports();
      })
      .catch((error) => {
        console.error('Delete failed:', error);
        setSnackbarMessage('Failed to delete Audit Report.');
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
      })
      .finally(() => {
        setDeleteDialogOpen(false);
        setDeleteItem(null);
      });
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.financial_establishment) errors.financial_establishment = 'FE Base is required';
    if (!formData.fy_year?.trim()) errors.fy_year = 'FY Year is required';
    if (!formData.file_type?.trim()) errors.file_type = 'File Type is required';
    if (!isEditMode && !formData.doc) errors.doc = 'Document upload is required';
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleFileChange = (e) => {
    if (e.target.files.length > 0) {
      setFormData({ ...formData, doc: e.target.files[0] });
    }
  };

  const handleFormSubmit = async () => {
    if (!validateForm()) return;

    const payload = new FormData();
    payload.append('financial_establishment', formData.financial_establishment);
    payload.append('fy_year', formData.fy_year);
    payload.append('file_type', formData.file_type);
    if (formData.doc) {
      payload.append('doc', formData.doc);
    }

    try {
      setUploading(true);
      setUploadProgress(0);

      if (isEditMode) {
        await api.patch(
          `/fe-mgmt/fe-audit-report/${selectedItem.id}/`,
          payload,
          {
            headers: { 'Content-Type': 'multipart/form-data' },
            onUploadProgress: (progressEvent) => {
              const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
              setUploadProgress(percent);
            },
          }
        );
        setSnackbarMessage('Audit Report updated successfully.');
      } else {
        await api.post(`/fe-mgmt/fe-audit-report/`, payload, {
          headers: { 'Content-Type': 'multipart/form-data' },
          onUploadProgress: (progressEvent) => {
            const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setUploadProgress(percent);
          },
        });
        setSnackbarMessage('Audit Report added successfully.');
      }

      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      fetchAuditReports();
      setOpenFormDialog(false);
    } catch (error) {
      console.error('Submit failed:', error);
      setSnackbarMessage('Failed to submit Audit Report.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  if (loading) {
    return <LoadingSpinner size={30} />;
  }

  return (
    <Box sx={{ width: '100%', mt: 8, mb: 1, borderRadius: 2, boxShadow: '0 4px 12px rgba(0,0,0,0.1)', backgroundColor: '#fff' }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" bgcolor="rgba(195,234,203,0.1)" p={2}>
        <Typography variant="h4" component="h1" fontWeight="bold">
          FE Audit Reports
        </Typography>
        <Button variant="contained" color="primary" onClick={handleAddItem} disabled={uploading}>
          Add
        </Button>
      </Box>
      <TableComponent
        columns={columns}
        rows={rows}
        manageColumnStatus
        enableSort
        sx={{ '& .MuiDataGrid-columnHeaders': { backgroundColor: '#1976d2', color: '#fff', fontWeight: 'bold' } }}
      />
      <Dialog open={openFormDialog} onClose={() => !uploading && setOpenFormDialog(false)}>
        <DialogTitle>{isEditMode ? 'Edit Audit Report' : 'Add Audit Report'}</DialogTitle>
        <DialogContent>
          <TextField
            select
            margin="dense"
            label="FE Base"
            fullWidth
            value={formData.financial_establishment}
            error={!!formErrors.financial_establishment}
            helperText={formErrors.financial_establishment}
            onChange={(e) => setFormData({ ...formData, financial_establishment: e.target.value })}
            disabled={uploading}
          >
            {feBaseList.map((option) => (
              <MenuItem key={option.id} value={option.id}>
                {option.name_of_fe || option.id}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            margin="dense"
            label="FY Year"
            fullWidth
            value={formData.fy_year}
            error={!!formErrors.fy_year}
            helperText={formErrors.fy_year}
            onChange={(e) => setFormData({ ...formData, fy_year: e.target.value })}
            disabled={uploading}
          />
          <TextField
            margin="dense"
            label="File Type"
            fullWidth
            value={formData.file_type}
            error={!!formErrors.file_type}
            helperText={formErrors.file_type}
            onChange={(e) => setFormData({ ...formData, file_type: e.target.value })}
            disabled={uploading}
          />
          <Button variant="outlined" component="label" sx={{ mt: 2 }}>
            {formData.doc ? formData.doc.name : 'Upload Document'}
            <input type="file" hidden onChange={handleFileChange} disabled={uploading} />
          </Button>
          {formErrors.doc && (
            <Typography color="error" variant="body2" sx={{ mt: 1 }}>
              {formErrors.doc}
            </Typography>
          )}
          {uploading && (
            <Box sx={{ mt: 2 }}>
              <LinearProgress variant="determinate" value={uploadProgress} />
              <Typography variant="body2" sx={{ textAlign: 'center', mt: 1 }}>
                Uploading... {uploadProgress}%
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => !uploading && setOpenFormDialog(false)} disabled={uploading}>
            Cancel
          </Button>
          <Button onClick={handleFormSubmit} variant="contained" color="primary" disabled={uploading}>
            {isEditMode ? 'Update' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>Are you sure you want to delete this Audit Report?</DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={uploading}>
            Cancel
          </Button>
          <Button onClick={handleDelete} variant="contained" color="error" disabled={uploading}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
      <SnackBarComponent open={snackbarOpen} message={snackbarMessage} severity={snackbarSeverity} onClose={handleSnackbarClose} />
    </Box>
  );
};
